import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import SelectAllIcon from '@mui/icons-material/CheckBoxOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/ShareOutlined';
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React from 'react';
import { Bookmark } from '../../../functions/src/types';
import { deleteBookmark } from '../ApiClient';
import { domainFromUrl } from '../Utils';
import { useUserContext } from '../context/UserContext';
import { useDataContext } from '../context/DataContext';
import { debuglog } from '../Log';
import { useFeedbackContext } from '../context/FeedbackContext';
import { useLocation, useNavigate } from 'react-router';

interface SiteCardProps {
  site: Bookmark;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelected?: (fid: string) => void;
}

export const SiteCard: React.FC<SiteCardProps> = ({ site, selectionMode = false, selected = false, onToggleSelected }) => {

  const { checkToken } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { removeBookmark } = useDataContext();
  const { showMessage, showErrorMessage } = useFeedbackContext();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const handleDelete = async () => {
    if (site && site.fid) {
      setDeleting(true);
      const checkedUser = await checkToken();
      if (checkedUser) {
        const result = await deleteBookmark(checkedUser, site.fid);
        if (result) {
          showMessage(`Deleted ${site.title}`);
          removeBookmark(site.url);
        }
        else {
          debuglog('failed to delete');
          showErrorMessage(`Failed to delete ${site.title}`);
        }
      }
      setDeleting(false);
    }
  };

  const shortenedUrl = domainFromUrl(site.url);
  return (
    <Box sx={{
      height: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1.5,
      transition: 'box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
      },
    }}
      maxHeight='360px' maxWidth='300px' minWidth='268px'
    >
      <Stack sx={{ p: 2.5, height: '100%', boxSizing: 'border-box' }}>
        <Link href={site.url} target='_blank' rel='noopener noreferrer' underline='none'
          sx={{ '&:hover': { textDecoration: 'underline', textDecorationColor: 'secondary.main' } }}
        >
          <Typography
            variant='h6'
            aria-label='title'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              maxWidth: '300px',
              lineHeight: 1.2,
              height: `${3 * 1.2}em`,
            }}
          >{site.title}</Typography>
        </Link>
        <Typography
          variant='caption'
          aria-label='date added'
          sx={{ color: 'text.secondary', mt: 1 }}
        >{site.addedOn.toLocaleDateString()}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, paddingBottom: '1em', marginTop: 1 }}>
          {site.favicon && (
            <img
              src={site.favicon}
              alt=""
              style={{
                width: '16px',
                height: '16px',
                objectFit: 'contain',
                flexShrink: 0,
                verticalAlign: 'middle',
                opacity: 0.7,
              }}
              onError={(e) => {
                // Hide favicon if it fails to load
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <Typography
            variant='caption'
            aria-label='url'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              color: 'text.secondary',
            }}
          >{shortenedUrl}</Typography>
        </Box>
        {site.tags && site.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 0.5 }}>
            {site.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                color="secondary"
                sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }}
                onClick={() => navigate(`/?tags=${encodeURIComponent(tag)}`)}
              />
            ))}
          </Box>
        )}
        <Typography
          variant='body1'
          aria-label='summary'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            color: 'text.secondary',
            flex: 1,
          }}
        >{site.summary || site.url}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box sx={{ minWidth: 40, display: 'flex', alignItems: 'center' }}>
            {selectionMode && site.fid && (
              <Checkbox
                checked={selected}
                onChange={() => onToggleSelected?.(site.fid!)}
                inputProps={{ 'aria-label': `select ${site.title}` }}
                sx={{ p: 0.5 }}
              />
            )}
          </Box>
          <IconButton aria-label='menu' size='small'
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
          >
            {!deleting && <MoreVertIcon />}
            {deleting && <CircularProgress size={20} />}
          </IconButton>
        </Box>
      </Stack>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setMenuAnchorEl(null);
          navigate({
            pathname: `/modify/${site.fid}`,
            search: location.search,
          });
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Modify</ListItemText>
        </MenuItem>
        {site.fid && (
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            onToggleSelected?.(site.fid!);
          }}>
            <ListItemIcon><SelectAllIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{selected ? 'Deselect' : 'Select'}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={async () => {
          setMenuAnchorEl(null);
          if (navigator.share) {
            try {
              await navigator.share({ title: site.title, url: site.url });
            } catch (err) {
              // User cancelled or share failed silently
            }
          } else {
            await navigator.clipboard.writeText(site.url);
            showMessage('Link copied to clipboard');
          }
        }}>
          <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchorEl(null);
          setConfirmDeleteOpen(true);
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="secondary" />
            Delete bookmark?
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '280px', sm: '100%' },
            }}
          >
            {site.title}
          </DialogContentText>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              display: 'block',
              mt: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '280px', sm: '100%' },
            }}
          >
            {site.url}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={() => {
            setConfirmDeleteOpen(false);
            handleDelete();
          }} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};
